import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoBagHandle,
  IoCheckmark,
  IoChevronDown,
  IoChevronUp,
  IoClose,
  IoEllipsisHorizontal,
  IoHelpCircle,
  IoLanguage,
  IoPeople,
  IoPlay,
  IoSettings,
  IoStatsChart,
  IoConstruct,
} from "react-icons/io5";
import "./LandingPage.css";

const LANDING_LANGUAGE_STORAGE_KEY = "xima_landing_language";
const LANDING_VIDEO_SRC = "/assets/videos/xima-landing.mp4";
const LANDING_VIDEO_LOOP_PAUSE_MS = 1000;
const LANDING_LANGUAGE_OPTIONS = [
  { value: "es", shortLabel: "ES", nativeLabel: "Español" },
  { value: "en", shortLabel: "EN", nativeLabel: "English" },
  { value: "de", shortLabel: "DE", nativeLabel: "Deutsch" },
  { value: "ru", shortLabel: "RU", nativeLabel: "Русский" },
];
const LANDING_LANGUAGES = new Set(
  LANDING_LANGUAGE_OPTIONS.map((option) => option.value)
);

const LANDING_COPY = {
  es: {
    navigationLabel: "Navegación principal",
    homeLabel: "XIMA, inicio",
    sectionsLabel: "Secciones de XIMA",
    play: "Jugar",
    puzzle: "Puzzle",
    learn: "Aprender",
    watch: "Ver",
    other: "Otros",
    design: "Modo diseñar",
    store: "Tienda",
    stats: "Estadísticas",
    friends: "Amigos",
    settings: "Ajustes",
    register: "Regístrate",
    login: "Iniciar sesión",
    help: "Ayuda y asistencia",
    language: "Idioma",
    eyebrow: "EL JUEGO DE ESTRATEGIA QUE SUBE DE NIVEL",
    titleLead: "¡Juega",
    titleTail: "online,",
    titleLineTwo: "novedoso juego",
    titleLineThree: "de Ciencia y Azar!",
    descriptionLines: [
      "Disfruta del novedoso juego XIMA.",
      "Diviértete, aprende y deja que tu mente",
      "llegue a la cima literalmente.",
    ],
    playNow: "Jugar ahora",
    languageDialogTitle: "Configuración de idioma",
    languageDialogDescription: "Elige el idioma de esta portada.",
    close: "Cerrar",
  },
  en: {
    navigationLabel: "Main navigation",
    homeLabel: "XIMA, home",
    sectionsLabel: "XIMA sections",
    play: "Play",
    puzzle: "Puzzle",
    learn: "Learn",
    watch: "Watch",
    other: "More",
    design: "Design mode",
    store: "Store",
    stats: "Statistics",
    friends: "Friends",
    settings: "Settings",
    register: "Sign up",
    login: "Log in",
    help: "Help and support",
    language: "Language",
    eyebrow: "THE STRATEGY GAME THAT REACHES NEW HEIGHTS",
    titleLead: "Play",
    titleTail: "online,",
    titleLineTwo: "an innovative game",
    titleLineThree: "of Science and Chance!",
    descriptionLines: [
      "Enjoy the innovative XIMA game.",
      "Have fun, learn, and let your mind",
      "literally reach the summit.",
    ],
    playNow: "Play now",
    languageDialogTitle: "Language settings",
    languageDialogDescription: "Choose the language for this landing page.",
    close: "Close",
  },
  de: {
    navigationLabel: "Hauptnavigation",
    homeLabel: "XIMA, Startseite",
    sectionsLabel: "XIMA-Bereiche",
    play: "Spielen",
    puzzle: "Puzzle",
    learn: "Lernen",
    watch: "Zusehen",
    other: "Mehr",
    design: "Designmodus",
    store: "Shop",
    stats: "Statistiken",
    friends: "Freunde",
    settings: "Einstellungen",
    register: "Registrieren",
    login: "Anmelden",
    help: "Hilfe und Support",
    language: "Sprache",
    eyebrow: "DAS STRATEGIESPIEL, DAS NEUE HÖHEN ERREICHT",
    titleLead: "Spiele",
    titleTail: "online,",
    titleLineTwo: "ein neuartiges Spiel",
    titleLineThree: "Wissenschaft & Zufall!",
    descriptionLines: [
      "Genieße das neuartige Spiel XIMA.",
      "Hab Spaß, lerne und lass deinen Geist",
      "buchstäblich den Gipfel erreichen.",
    ],
    playNow: "Jetzt spielen",
    languageDialogTitle: "Spracheinstellungen",
    languageDialogDescription: "Wähle die Sprache dieser Startseite.",
    close: "Schließen",
  },
  ru: {
    navigationLabel: "Главная навигация",
    homeLabel: "XIMA, главная",
    sectionsLabel: "Разделы XIMA",
    play: "Играть",
    puzzle: "Головоломка",
    learn: "Обучение",
    watch: "Смотреть",
    other: "Ещё",
    design: "Режим дизайна",
    store: "Магазин",
    stats: "Статистика",
    friends: "Друзья",
    settings: "Настройки",
    register: "Регистрация",
    login: "Войти",
    help: "Помощь и поддержка",
    language: "Язык",
    eyebrow: "СТРАТЕГИЧЕСКАЯ ИГРА, ВЕДУЩАЯ К НОВЫМ ВЕРШИНАМ",
    titleLead: "Играйте в",
    titleTail: "онлайн —",
    titleLineTwo: "новаторскую игру",
    titleLineThree: "Наука и Случай!",
    descriptionLines: [
      "Откройте для себя новаторскую игру XIMA.",
      "Играйте, учитесь и позвольте разуму",
      "буквально достичь вершины.",
    ],
    playNow: "Играть сейчас",
    languageDialogTitle: "Настройка языка",
    languageDialogDescription: "Выберите язык этой главной страницы.",
    close: "Закрыть",
  },
};

const NavBadge = ({ id, from, to, className, children }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id={`xima-badge-${id}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={from} />
        <stop offset="100%" stopColor={to} />
      </linearGradient>
      <linearGradient id={`xima-badge-${id}-shine`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.38" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
    </defs>
    <circle cx="24" cy="24" r="22" fill={`url(#xima-badge-${id})`} />
    <ellipse cx="24" cy="14" rx="15" ry="9" fill={`url(#xima-badge-${id}-shine)`} />
    <circle cx="24" cy="24" r="21.25" fill="none" stroke="#ffffff" strokeOpacity="0.22" strokeWidth="1.5" />
    {children}
  </svg>
);

const PlayBadge = ({ className }) => (
  <NavBadge id="play" from="#34e89e" to="#0f9b58" className={className}>
    <path d="M19.5 15.2c0-1.3 1.4-2.1 2.5-1.4l12.2 7.8c1 .7 1 2.2 0 2.8L22 32.2c-1.1.7-2.5-.1-2.5-1.4z" fill="#ffffff" />
  </NavBadge>
);

const PuzzleBadge = ({ className }) => (
  <NavBadge id="puzzle" from="#ffb347" to="#ff6a00" className={className}>
    <path
      d="M14 18.5h5.2a3.6 3.6 0 1 1 6.6 0H31v6a3.6 3.6 0 1 1 0 6.6V35H14v-5.2a3.4 3.4 0 1 0 0-6.2z"
      fill="#ffffff"
    />
  </NavBadge>
);

const LearnBadge = ({ className }) => (
  <NavBadge id="learn" from="#6ec6ff" to="#1565c0" className={className}>
    <path d="M24 13.5 37 20l-13 6.5L11 20z" fill="#ffffff" />
    <path d="M16.5 23.6v5.4c0 2 3.4 4 7.5 4s7.5-2 7.5-4v-5.4L24 27.4z" fill="#ffffff" />
    <path d="M35 21v7.2" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="35" cy="29.6" r="1.7" fill="#ffffff" />
  </NavBadge>
);

const WatchBadge = ({ className }) => (
  <NavBadge id="watch" from="#fff176" to="#f9a825" className={className}>
    <path
      d="M10.5 24c3.2-5.6 8-8.4 13.5-8.4s10.3 2.8 13.5 8.4c-3.2 5.6-8 8.4-13.5 8.4S13.7 29.6 10.5 24z"
      fill="#ffffff"
    />
    <circle cx="24" cy="24" r="5.2" fill="#7a4a00" />
    <circle cx="25.8" cy="22.3" r="1.6" fill="#ffffff" />
  </NavBadge>
);

const PRIMARY_LINKS = [
  { copyKey: "play", to: "/guest-launch/jugar", icon: PlayBadge, accent: "green" },
  {
    copyKey: "puzzle",
    to: "/guest-launch/puzzle",
    icon: PuzzleBadge,
    accent: "orange",
  },
  {
    copyKey: "learn",
    to: "/guest-launch/aprender",
    icon: LearnBadge,
    accent: "blue",
  },
  { copyKey: "watch", to: "/game/spectate", icon: WatchBadge, accent: "yellow" },
];

const OTHER_LINKS = [
  { copyKey: "design", to: "/game/design", icon: IoConstruct },
  { copyKey: "store", to: "/game/store", icon: IoBagHandle },
  { copyKey: "stats", to: "/game/stats", icon: IoStatsChart },
  { copyKey: "friends", to: "/game/friends", icon: IoPeople },
  { copyKey: "settings", to: "/game/settings?section=general", icon: IoSettings },
];

const getInitialLandingLanguage = () => {
  if (typeof window === "undefined") return "es";
  try {
    const storedLanguage = String(
      window.localStorage.getItem(LANDING_LANGUAGE_STORAGE_KEY) || ""
    )
      .trim()
      .toLowerCase();
    return LANDING_LANGUAGES.has(storedLanguage) ? storedLanguage : "es";
  } catch {
    return "es";
  }
};

const LandingPage = () => {
  const [otherMenuOpen, setOtherMenuOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [language, setLanguage] = useState(getInitialLandingLanguage);
  const copy = LANDING_COPY[language] || LANDING_COPY.es;
  const boardVideoRef = useRef(null);
  const boardVideoRestartRef = useRef(null);

  useEffect(() => {
    const video = boardVideoRef.current;
    if (video) {
      // React does not always write the muted attribute; browsers need it for autoplay.
      video.muted = true;
      video.defaultMuted = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    }
    return () => window.clearTimeout(boardVideoRestartRef.current);
  }, []);

  const handleBoardVideoEnded = () => {
    window.clearTimeout(boardVideoRestartRef.current);
    boardVideoRestartRef.current = window.setTimeout(() => {
      const video = boardVideoRef.current;
      if (!video) return;
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    }, LANDING_VIDEO_LOOP_PAUSE_MS);
  };

  useEffect(() => {
    if (!languageDialogOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setLanguageDialogOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [languageDialogOpen]);

  const changeLandingLanguage = (nextLanguage) => {
    if (!LANDING_LANGUAGES.has(nextLanguage)) return;
    setLanguage(nextLanguage);
    try {
      window.localStorage.setItem(LANDING_LANGUAGE_STORAGE_KEY, nextLanguage);
    } catch {
      // The selection still works for this visit if storage is unavailable.
    }
    setLanguageDialogOpen(false);
  };

  return (
    <main className="xima-landing" lang={language} data-language={language}>
      <aside className="xima-landing-sidebar" aria-label={copy.navigationLabel}>
        <Link className="xima-landing-logo-link" to="/" aria-label={copy.homeLabel}>
          <img
            className="xima-landing-logo"
            src="/mejor-logo-336.png"
            alt="XIMA"
          />
        </Link>

        <nav className="xima-landing-nav" aria-label={copy.sectionsLabel}>
          {PRIMARY_LINKS.map(({ copyKey, to, icon: Icon, accent }) => (
            <Link className="xima-landing-nav-link" to={to} key={copyKey}>
              <Icon
                className={`xima-landing-nav-icon xima-landing-nav-icon-${accent}`}
                aria-hidden="true"
              />
              <span>{copy[copyKey]}</span>
            </Link>
          ))}

          <button
            className="xima-landing-nav-link xima-landing-other-button"
            type="button"
            aria-expanded={otherMenuOpen}
            aria-controls="xima-landing-other-menu"
            onClick={() => setOtherMenuOpen((current) => !current)}
          >
            <IoEllipsisHorizontal
              className="xima-landing-nav-icon xima-landing-nav-icon-muted"
              aria-hidden="true"
            />
            <span>{copy.other}</span>
            {otherMenuOpen ? (
              <IoChevronUp className="xima-landing-other-chevron" aria-hidden="true" />
            ) : (
              <IoChevronDown className="xima-landing-other-chevron" aria-hidden="true" />
            )}
          </button>

          {otherMenuOpen ? (
            <div id="xima-landing-other-menu" className="xima-landing-other-menu is-open">
              {OTHER_LINKS.map(({ copyKey, to, icon: Icon }) => (
                <Link className="xima-landing-other-link" to={to} key={copyKey}>
                  <Icon aria-hidden="true" />
                  <span>{copy[copyKey]}</span>
                </Link>
              ))}
            </div>
          ) : null}
        </nav>

        <div className="xima-landing-sidebar-footer">
          <Link className="xima-landing-auth-button is-register" to="/register">
            {copy.register}
          </Link>
          <Link className="xima-landing-auth-button is-login" to="/login">
            {copy.login}
          </Link>

          <Link
            className="xima-landing-footer-link"
            to="/game/settings?section=support"
          >
            <IoHelpCircle aria-hidden="true" />
            <span>{copy.help}</span>
          </Link>
          <button
            className="xima-landing-footer-link xima-landing-language-button"
            type="button"
            aria-haspopup="dialog"
            onClick={() => setLanguageDialogOpen(true)}
          >
            <IoLanguage aria-hidden="true" />
            <span>{copy.language}</span>
          </button>
        </div>
      </aside>

      <section className="xima-landing-hero" aria-labelledby="xima-landing-title">
        <div className="xima-landing-board" aria-hidden="true">
          <video
            ref={boardVideoRef}
            className="xima-landing-board-video"
            src={LANDING_VIDEO_SRC}
            autoPlay
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            onEnded={handleBoardVideoEnded}
          />
        </div>

        <div className="xima-landing-copy">
          <p className="xima-landing-eyebrow">{copy.eyebrow}</p>
          <h1 id="xima-landing-title">
            <span className="xima-landing-title-line">
              {copy.titleLead} <strong>XIMA</strong> {copy.titleTail}
            </span>
            <span className="xima-landing-title-line">{copy.titleLineTwo}</span>
            <span className="xima-landing-title-line">{copy.titleLineThree}</span>
          </h1>
          <div className="xima-landing-title-rule" aria-hidden="true" />
          <p className="xima-landing-description">
            {copy.descriptionLines.map((line, index) => (
              <React.Fragment key={line}>
                {index > 0 ? <br /> : null}
                {line}
              </React.Fragment>
            ))}
          </p>
          <Link className="xima-landing-play-now" to="/login">
            <IoPlay aria-hidden="true" />
            <span>{copy.playNow}</span>
          </Link>
        </div>
      </section>

      {languageDialogOpen ? (
        <div
          className="xima-landing-language-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLanguageDialogOpen(false);
          }}
        >
          <section
            className="xima-landing-language-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="xima-landing-language-title"
          >
            <button
              className="xima-landing-language-close"
              type="button"
              aria-label={copy.close}
              onClick={() => setLanguageDialogOpen(false)}
            >
              <IoClose aria-hidden="true" />
            </button>
            <IoLanguage className="xima-landing-language-dialog-icon" aria-hidden="true" />
            <h2 id="xima-landing-language-title">{copy.languageDialogTitle}</h2>
            <p>{copy.languageDialogDescription}</p>
            <div className="xima-landing-language-options">
              {LANDING_LANGUAGE_OPTIONS.map((option) => {
                const active = option.value === language;
                return (
                  <button
                    className={`xima-landing-language-option${active ? " is-active" : ""}`}
                    type="button"
                    key={option.value}
                    aria-pressed={active}
                    onClick={() => changeLandingLanguage(option.value)}
                  >
                    <span className="xima-landing-language-code">{option.shortLabel}</span>
                    <span>{option.nativeLabel}</span>
                    {active ? <IoCheckmark aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
};

export default LandingPage;
